package ai.jessie.web.work

import ai.jessie.shared.work.SaveWorkRequest
import ai.jessie.shared.work.WorkCalendarResponse
import ai.jessie.shared.work.WorkCalendarView
import ai.jessie.shared.work.WorkProblemResponse
import ai.jessie.shared.work.WorkResponse
import ai.jessie.shared.work.WorkSelectionsResponse
import ai.jessie.web.onboarding.apiRequest
import ai.jessie.web.onboarding.decodeProblem
import ai.jessie.web.onboarding.validOrganisationId
import androidx.compose.runtime.staticCompositionLocalOf
import kotlinx.serialization.json.Json

// UC-008. A null work ID addresses the collection: saving creates a work entry.
interface WorkPort {
    suspend fun list(organisationId: String): List<WorkResponse>

    suspend fun selections(organisationId: String): WorkSelectionsResponse

    suspend fun read(organisationId: String, workId: String): WorkResponse

    suspend fun save(
        organisationId: String,
        workId: String?,
        request: SaveWorkRequest,
    ): WorkResponse
}

val LocalWorkPort =
    staticCompositionLocalOf<WorkPort> { error("Work port must be supplied by the application") }

class WorkRequestFailure(val status: Int, val detail: WorkProblemResponse) :
    Exception("Work request failed")

class BrowserWorkPort : WorkPort, WorkCalendarPort {
    private val json = Json { ignoreUnknownKeys = true }

    override suspend fun list(organisationId: String) =
        json.decodeFromString<List<WorkResponse>>(request(organisationId, ""))

    override suspend fun selections(organisationId: String) =
        json.decodeFromString<WorkSelectionsResponse>(request(organisationId, "/selections"))

    override suspend fun read(organisationId: String, workId: String) =
        json.decodeFromString<WorkResponse>(request(organisationId, child(workId)))

    override suspend fun save(organisationId: String, workId: String?, request: SaveWorkRequest) =
        json.decodeFromString<WorkResponse>(
            request(organisationId, child(workId), json.encodeToString(request))
        )

    override suspend fun calendar(
        organisationId: String,
        view: WorkCalendarView?,
        date: String?,
    ): WorkCalendarResponse {
        require(date == null || validCalendarDate(date))
        val query = listOfNotNull(view?.let { "view=${it.name}" }, date?.let { "date=$it" })
        val path = if (query.isEmpty()) "/calendar" else "/calendar?${query.joinToString("&")}"
        return json.decodeFromString<WorkCalendarResponse>(request(organisationId, path))
    }

    private fun child(id: String?): String {
        if (id == null) return ""
        require(validWorkId(id))
        return "/$id"
    }

    private suspend fun request(
        organisationId: String,
        path: String,
        body: String? = null,
    ): String {
        require(validOrganisationId(organisationId))
        return apiRequest(json, "/organisations/$organisationId/work$path", body) { status, text ->
            val detail = json.decodeProblem(text, WorkProblemResponse(""))
            WorkRequestFailure(status, detail)
        }
    }
}

// Work public IDs share the organisation ID format.
fun validWorkId(value: String) = validOrganisationId(value)
